import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Table, Row, Col, Form, InputGroup, Button, Badge, 
    Modal, Spinner, Alert, Tooltip, OverlayTrigger, Pagination 
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import './Factures.css';

export default function Avoirs() {
    const navigate = useNavigate();
    const [avoirs, setAvoirs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedAvoir, setSelectedAvoir] = useState(null);
    const [selectedAvoirLignes, setSelectedAvoirLignes] = useState([]);
    const [loadingLignes, setLoadingLignes] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'date_facture', direction: 'desc' });
      const [societeInfos, setSocieteInfos] = useState(null);
    // États pour les taxes dynamiques
    const [taxes, setTaxes] = useState({
        tva_taux: 18,
        css_taux: 1,
        tva_active: true,
        css_active: true
    });

    // États pour la pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchAvoirs();
        fetchStats();
        fetchTaxes(); // Charger les taxes
        fetchSocieteInfos();
    }, []);


    const fetchSocieteInfos = async () => {
    try {
        const token = localStorage.getItem("token");
        const response = await fetch(
            'https://sawd.julesferry-ga.com/api/parametres.php?action=societe',
            { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        if (response.ok) {
            const result = await response.json();
            if (result.data) {
                setSocieteInfos(result.data);
            }
        }
    } catch (err) {
        console.error("Erreur chargement société", err);
    }
};
    // Charger les taxes
    const fetchTaxes = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/parametres.php?action=taxes',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) {
                    setTaxes(result.data);
                }
            }
        } catch (err) {
            console.error("Erreur chargement taxes", err);
        }
    };

    const fetchAvoirs = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/factures.php?type=avoir',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setAvoirs(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Charger les lignes d'un avoir
    const fetchAvoirLignes = async (idAvoir) => {
        setLoadingLignes(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/factures.php?id=${idAvoir}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setSelectedAvoirLignes(result.data?.lignes || []);
        } catch (err) {
            console.error("Erreur chargement lignes:", err);
            setSelectedAvoirLignes([]);
        } finally {
            setLoadingLignes(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/factures.php?action=stats',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    // Filtrage des avoirs
    const filteredAvoirs = useMemo(() => {
        return avoirs
            .filter(a => 
                a.numero_facture?.toLowerCase().includes(filterText.toLowerCase()) ||
                a.client_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
                a.notes?.toLowerCase().includes(filterText.toLowerCase())
            )
            .sort((a, b) => {
                if (sortConfig.key === 'date_facture') {
                    return sortConfig.direction === 'asc'
                        ? new Date(a.date_facture) - new Date(b.date_facture)
                        : new Date(b.date_facture) - new Date(a.date_facture);
                }
                if (sortConfig.key === 'montant') {
                    const montantA = a.net_a_payer || 0;
                    const montantB = b.net_a_payer || 0;
                    return sortConfig.direction === 'asc'
                        ? montantA - montantB
                        : montantB - montantA;
                }
                return 0;
            });
    }, [avoirs, filterText, sortConfig]);

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredAvoirs.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredAvoirs.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        for (let number = 1; number <= totalPages; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }
        return items;
    };

    const totalAvoirs = filteredAvoirs.reduce((acc, a) => {
        if (typeof a.net_a_payer === 'number') {
            return acc + a.net_a_payer;
        }
        const montant = parseFloat(String(a.net_a_payer).replace(/[^\d.-]/g, '')) || 0;
        return acc + montant;
    }, 0);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );


// Fonction pour imprimer un avoir (même format que facture avec logo)
const handlePrintAvoir = async (avoir) => {
    // Si les lignes ne sont pas chargées, on les charge d'abord
    let avoirComplet = avoir;
    if (!avoir.lignes || avoir.lignes.length === 0) {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/factures.php?id=${avoir.id_facture}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                avoirComplet = result.data;
            }
        } catch (err) {
            console.error("Erreur chargement lignes pour impression:", err);
        }
    }

    // Créer une nouvelle fenêtre pour l'impression
    const printWindow = window.open('', '_blank');
    
    // Récupérer les informations de la société
    const company = {
        name: societeInfos?.nom || 'GESTION STOCK',
        address: societeInfos?.adresse || '123 Rue du Commerce, Libreville',
        phone: societeInfos?.telephone || '+241 01 23 45 67',
        email: societeInfos?.email || 'contact@gestionstock.ga',
        site_web: societeInfos?.site_web || 'www.gestionstock.ga',
        rc: societeInfos?.rc || 'LB/2025/00123',
        nif: societeInfos?.nif || '20251234567',
        patente: societeInfos?.patente || 'PAT0012345',
        logo_url: societeInfos?.logo_url 
    };

    // Calculer les totaux
    const totalHT = avoirComplet.montant_ht || 0;
    const totalTVA = avoirComplet.montant_tva || 0;
    const totalCSS = avoirComplet.montant_css || 0;
    const totalTTC = avoirComplet.net_a_payer || 0;

    const logoText = company.name.substring(0, 2).toUpperCase();
    const logoSrc = company.logo_url || '';

    // Styles CSS pour l'impression (version avoir)
    const printStyles = `
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        body {
            background: #f5f7fa;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
            padding: 30px;
            color: #2c3e50;
        }

        .document-paper {
            max-width: 900px;
            margin: 0 auto;
            background: white;
            box-shadow: 0 5px 20px rgba(0,0,0,0.1);
            padding: 30px 35px;
            border-radius: 8px;
        }

        /* En-tête */
        .document-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 25px;
            padding-bottom: 20px;
            border-bottom: 2px solid #f59e0b;
        }

        .company-info h1 {
            font-size: 1.8rem;
            font-weight: 700;
            color: #1e293b;
            margin-bottom: 5px;
        }

        .company-details {
            font-size: 0.85rem;
            color: #5a6a7e;
            line-height: 1.5;
            margin-top: 5px;
        }

        .company-details span {
            display: inline-block;
            margin-right: 20px;
        }

        .document-badge {
            text-align: right;
        }

        .document-badge .doc-type {
            font-size: 1.2rem;
            font-weight: 600;
            color: #f59e0b;
            margin-bottom: 5px;
        }

        .document-badge .doc-number {
            font-size: 0.9rem;
            color: #5a6a7e;
            background: #f1f5f9;
            padding: 4px 12px;
            border-radius: 4px;
            display: inline-block;
        }

        .status-badge {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 4px;
            font-size: 0.8rem;
            font-weight: 600;
            text-transform: uppercase;
            margin-top: 8px;
            background: #f59e0b;
            color: white;
        }

        /* Section client */
        .client-section {
            background: #f8fafc;
            padding: 20px;
            border-radius: 6px;
            margin: 20px 0;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
        }

        .client-info h3 {
            font-size: 0.8rem;
            font-weight: 600;
            color: #f59e0b;
            text-transform: uppercase;
            letter-spacing: 0.3px;
            margin-bottom: 12px;
        }

        .client-name {
            font-size: 1.2rem;
            font-weight: 600;
            color: #1e293b;
            margin-bottom: 8px;
        }

        .client-details {
            font-size: 0.85rem;
            color: #4a5a6e;
            line-height: 1.6;
        }

        .client-details span {
            display: block;
        }

        .document-dates {
            text-align: right;
        }

        .date-item {
            margin-bottom: 8px;
        }

        .date-item .label {
            font-size: 0.75rem;
            color: #6f7d95;
            display: block;
        }

        .date-item .value {
            font-size: 1rem;
            font-weight: 500;
            color: #1e293b;
        }

        .date-item.expired .value {
            color: #dc2626;
        }

        .validity-badge {
            font-size: 0.8rem;
            padding: 4px 10px;
            background: #fef3c7;
            color: #92400e;
            border-radius: 4px;
            display: inline-block;
            margin-top: 5px;
        }

        .validity-badge.expired {
            background: #fee2e2;
            color: #991b1b;
        }

        /* Notes / Motif */
        .notes-section {
            background: #fef3c7;
            padding: 12px 15px;
            border-left: 3px solid #f59e0b;
            margin: 20px 0;
            font-size: 0.9rem;
            color: #92400e;
        }

        .notes-section strong {
            color: #b45309;
            margin-right: 8px;
        }

        /* Tableau */
        .table-container {
            margin: 25px 0;
            border: 1px solid #e2e8f0;
            border-radius: 6px;
            overflow: hidden;
        }

        table {
            width: 100%;
            border-collapse: collapse;
            font-size: 0.85rem;
        }

        th {
            background: #f1f5f9;
            color: #1e293b;
            font-weight: 600;
            padding: 12px 10px;
            text-align: left;
            border-bottom: 1px solid #cbd5e1;
        }

        td {
            padding: 10px;
            border-bottom: 1px solid #e2e8f0;
        }

        tr:last-child td {
            border-bottom: none;
        }

        .ref-cell {
            font-family: 'Courier New', monospace;
            font-size: 0.8rem;
            color: #f59e0b;
            background: #fef3c7;
            padding: 3px 8px;
            border-radius: 3px;
            display: inline-block;
        }

        .amount-cell {
            text-align: right;
            font-weight: 500;
        }

        .text-center {
            text-align: center;
        }

        /* Totaux */
        .totals-wrapper {
            display: flex;
            justify-content: flex-end;
            margin-top: 20px;
        }

        .totals-card {
            width: 350px;
        }

        .total-line {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            border-bottom: 1px dashed #d1d9e6;
        }

        .total-line:last-child {
            border-bottom: none;
        }

        .total-line .label {
            color: #4a5a6e;
        }

        .total-line .value {
            font-weight: 500;
        }

        .total-line.highlight {
            background: #fef3c7;
            margin-top: 8px;
            padding: 12px;
            border-radius: 4px;
            font-weight: 600;
        }

        .total-line.highlight .label,
        .total-line.highlight .value {
            color: #f59e0b;
            font-size: 1.1rem;
        }

        /* Pied de page */
        .document-footer {
            margin-top: 35px;
            padding-top: 15px;
            border-top: 1px solid #d1d9e6;
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 0.75rem;
            color: #7f8c9f;
        }

        .legal-info span {
            margin-right: 20px;
        }

        .signature-area {
            text-align: right;
        }

        .signature-line {
            margin-top: 5px;
            font-style: italic;
        }

        .logo {
            width: 80px;
            height: 80px;
            object-fit: contain;
        }
        .logo-fallback {
            width: 80px;
            height: 80px;
            background: linear-gradient(135deg, #f59e0b, #d97706);
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 2rem;
            border-radius: 8px;
            box-shadow: 0 2px 10px rgba(245, 158, 11, 0.3);
        }

        @media print {
            body {
                background: white;
                padding: 0;
            }
            .document-paper {
                box-shadow: none;
                padding: 20px;
            }
            th {
                background: #f1f5f9 !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }
        }
    `;

    // Générer le HTML de l'avoir
    const printContent = `
        <!DOCTYPE html>
        <html lang="fr">
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Avoir ${avoirComplet.numero_facture}</title>
            <style>
                ${printStyles}
            </style>
        </head>
        <body>
            <div class="document-paper">
                <!-- En-tête avec logo -->
                <div class="document-header" style="display: flex; justify-content: space-between; align-items: flex-start;">
                    <div style="display: flex; align-items: center; gap: 15px;">
                        ${logoSrc ? `
                            <img src="${logoSrc}" alt="Logo ${company.name}" class="logo" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
                            <div class="logo-fallback" style="display: none;">${logoText}</div>
                        ` : `
                            <div class="logo-fallback">${logoText}</div>
                        `}
                        <div>
                            <h1 style="font-size: 1.8rem; font-weight: 700; color: #1e293b; margin: 0;">${company.name}</h1>
                            <div style="font-size: 0.8rem; color: #64748b;">${company.site_web}</div>
                        </div>
                    </div>
                    <div class="document-badge">
                        <div class="doc-type">AVOIR</div>
                        <div class="doc-number">N° ${avoirComplet.numero_facture}</div>
                        <div class="status-badge">NOTE DE CRÉDIT</div>
                    </div>
                </div>

                <!-- Coordonnées société -->
                <div style="font-size: 0.85rem; color: #5a6a7e; margin: 15px 0 20px 0; padding-bottom: 15px; border-bottom: 2px solid #f59e0b;">
                    <span style="margin-right: 20px;">| ${company.address}</span>
                    <span style="margin-right: 20px;">| ${company.phone}</span>
                    <span style="margin-right: 20px;">| ✉ ${company.email}</span>
                </div>

                <!-- Client -->
                <div class="client-section">
                    <div class="client-info">
                        <h3>CLIENT</h3>
                        <div class="client-name">${avoirComplet.client_nom || 'Client non spécifié'}</div>
                        <div class="client-details">
                            <span>Code: ${avoirComplet.code_client || ''}</span>
                            ${avoirComplet.client_telephone ? `<span> ${avoirComplet.client_telephone}</span>` : ''}
                            ${avoirComplet.client_email ? `<span>✉ ${avoirComplet.client_email}</span>` : ''}
                        </div>
                    </div>
                    <div class="document-dates">
                        <div class="date-item">
                            <span class="label">Date d'émission</span>
                            <span class="value">${formatDate(avoirComplet.date_facture)}</span>
                        </div>
                    </div>
                </div>

                <!-- Motif -->
                ${avoirComplet.notes ? `
                    <div class="notes-section">
                        <strong>Motif :</strong> ${avoirComplet.notes}
                    </div>
                ` : ''}

                <!-- Tableau des produits -->
                <div class="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th>Réf.</th>
                                <th>Désignation</th>
                                <th class="text-center">Qté</th>
                                <th class="amount-cell">Prix HT</th>
                                <th class="amount-cell">Total HT</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${avoirComplet.lignes && avoirComplet.lignes.length > 0 
                                ? avoirComplet.lignes.map(ligne => `
                                    <tr>
                                        <td><span class="ref-cell">${ligne.reference || ''}</span></td>
                                        <td>${ligne.designation || ''}</td>
                                        <td class="text-center">${ligne.quantite || 0}</td>
                                        <td class="amount-cell">${formatCurrency(ligne.prix_unitaire_ht || 0)}</td>
                                        <td class="amount-cell">${formatCurrency((ligne.quantite || 0) * (ligne.prix_unitaire_ht || 0))}</td>
                                    </tr>
                                `).join('')
                                : '<tr><td colspan="5" style="text-align: center; padding: 20px;">Aucun produit dans cet avoir</td></tr>'
                            }
                        </tbody>
                    </table>
                </div>

                <!-- Totaux -->
                <div class="totals-wrapper">
                    <div class="totals-card">
                        <div class="total-line">
                            <span class="label">Total HT</span>
                            <span class="value">${formatCurrency(totalHT)}</span>
                        </div>
                        ${totalTVA > 0 ? `
                            <div class="total-line">
                                <span class="label">TVA (${taxes.tva_active ? taxes.tva_taux : 0}%)</span>
                                <span class="value">${formatCurrency(totalTVA)}</span>
                            </div>
                        ` : ''}
                        ${totalCSS > 0 ? `
                            <div class="total-line">
                                <span class="label">CSS (${taxes.css_active ? taxes.css_taux : 0}%)</span>
                                <span class="value">${formatCurrency(totalCSS)}</span>
                            </div>
                        ` : ''}
                        <div class="total-line highlight">
                            <span class="label">TOTAL TTC</span>
                            <span class="value">-${formatCurrency(totalTTC)}</span>
                        </div>
                    </div>
                </div>

                <!-- Pied de page -->
                <div class="document-footer">
                    <div class="legal-info">
                        <span>RC: ${company.rc}</span>
                        <span>NIF: ${company.nif}</span>
                        <span>Patente: ${company.patente}</span>
                    </div>
                    <div class="signature-area">
                        <div>Cachet et signature</div>
                        <div class="signature-line">____________________</div>
                    </div>
                </div>
            </div>
            
            <script>
                window.onload = function() {
                    window.print();
                }
            </script>
        </body>
        </html>
    `;

    // Écrire le contenu dans la nouvelle fenêtre
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Attendre que les images chargent
    await new Promise((resolve) => {
        const images = printWindow.document.images;
        const totalImages = images.length;
        let loadedImages = 0;
        
        if (totalImages === 0) {
            resolve();
            return;
        }
        
        Array.from(images).forEach(img => {
            if (img.complete) {
                loadedImages++;
                if (loadedImages === totalImages) resolve();
            } else {
                img.onload = () => {
                    loadedImages++;
                    if (loadedImages === totalImages) resolve();
                };
                img.onerror = () => {
                    loadedImages++;
                    if (loadedImages === totalImages) resolve();
                };
            }
        });
    });
    
    // Une fois toutes les images chargées, imprimer
    printWindow.focus();
    printWindow.print();
};



    const ActionButton = ({ variant, icon, onClick, tooltip, disabled = false }) => (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-${variant}`}>{tooltip}</Tooltip>}
        >
            <Button
                variant={variant}
                size="sm"
                onClick={onClick}
                disabled={disabled}
                className="action-btn"
            >
                <i className={icon}></i>
            </Button>
        </OverlayTrigger>
    );

    if (loading) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="loading-container">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Chargement des avoirs...</p>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    }

    if (error) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error}</h4>
                        <Button 
                            variant="outline-danger" 
                            className="mt-3"
                            onClick={fetchAvoirs}
                        >
                            <i className="ri-refresh-line me-2"></i>
                            Réessayer
                        </Button>
                    </Alert>
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="page-icon bg-warning-soft">
                                        <i className="ri-file-copy-line text-warning"></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1">Gestion des avoirs</h1>
                                        <p className="page-subtitle mb-0">
                                            Consultez et gérez les avoirs et notes de crédit
                                        </p>
                                    </div>
                                </div>
                                <Button 
                                    variant="primary" 
                                    onClick={() => navigate('/ventes/factures')}
                                    className="btn-gradient px-4 py-2"
                                >
                                    <i className="ri-arrow-left-line me-2"></i>
                                    Retour aux factures
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Statistiques avec taxes dynamiques */}
                    <Row className="g-3 mb-4 stats-row">
                        <Col sm={6} lg={3}>
                            <Card className="stat-card border-0 shadow-sm">
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center">
                                        <div className="stat-icon-wrapper bg-warning me-3">
                                            <i className="ri-file-copy-line fs-2 text-white"></i>
                                        </div>
                                        <div className="stat-content">
                                            <span className="stat-label">Total avoirs</span>
                                            <h2 className="stat-value text-warning">
                                                {formatCurrency(totalAvoirs)}
                                            </h2>
                                            <small className="stat-trend">
                                                <i className="ri-file-list-3-line text-warning me-1"></i>
                                                {filteredAvoirs.length} avoir(s)
                                            </small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col sm={6} lg={3}>
                            <Card className="stat-card border-0 shadow-sm">
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center">
                                        <div className="stat-icon-wrapper bg-info me-3">
                                            <i className="ri-bar-chart-2-line fs-2 text-white"></i>
                                        </div>
                                        <div className="stat-content">
                                            <span className="stat-label">Moy. par avoir</span>
                                            <h2 className="stat-value text-info">
                                                {filteredAvoirs.length > 0 
                                                    ? formatCurrency(totalAvoirs / filteredAvoirs.length)
                                                    : formatCurrency(0)}
                                            </h2>
                                            <small className="stat-trend">
                                                <i className="ri-bar-chart-2-line text-info me-1"></i>
                                                Montant moyen
                                            </small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        {stats && (
                            <>
                                <Col sm={6} lg={3}>
                                    <Card className="stat-card border-0 shadow-sm">
                                        <Card.Body className="p-3">
                                            <div className="d-flex align-items-center">
                                                <div className="stat-icon-wrapper bg-primary me-3">
                                                    <i className="ri-group-line fs-2 text-white"></i>
                                                </div>
                                                <div className="stat-content">
                                                    <span className="stat-label">Clients concernés</span>
                                                    <h2 className="stat-value text-primary">
                                                        {new Set(avoirs.map(a => a.id_client)).size}
                                                    </h2>
                                                    <small className="stat-trend">
                                                        <i className="ri-user-line text-primary me-1"></i>
                                                        différents clients
                                                    </small>
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>

                                <Col sm={6} lg={3}>
                                    <Card className="stat-card border-0 shadow-sm">
                                        <Card.Body className="p-3">
                                            <div className="d-flex align-items-center">
                                                <div className="stat-icon-wrapper bg-success me-3">
                                                    <i className="ri-percent-line fs-2 text-white"></i>
                                                </div>
                                                <div className="stat-content">
                                                    <span className="stat-label">TVA totale</span>
                                                    <h2 className="stat-value text-success">
                                                        {formatCurrency(avoirs.reduce((acc, a) => {
                                                            const montantTVA = typeof a.montant_tva === 'number' 
                                                                ? a.montant_tva 
                                                                : parseFloat(String(a.montant_tva).replace(/[^\d.-]/g, '')) || 0;
                                                            return acc + montantTVA;
                                                        }, 0))}
                                                    </h2>
                                                    <small className="stat-trend">
                                                        <i className="ri-percent-line text-success me-1"></i>
                                                        {taxes.tva_active ? taxes.tva_taux : 0}% moyen
                                                    </small>
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </>
                        )}
                    </Row>

                    {/* Filtres */}
                    <Card className="filter-card border-0 mb-4">
                        <Card.Body>
                            <Row className="g-3">
                                <Col md={8}>
                                    <InputGroup className="search-input">
                                        <InputGroup.Text>
                                            <i className="ri-search-line"></i>
                                        </InputGroup.Text>
                                        <Form.Control
                                            placeholder="Rechercher avoir, client, motif..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                        />
                                        {filterText && (
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => setFilterText('')}
                                                className="border-0"
                                            >
                                                <i className="ri-close-line"></i>
                                            </Button>
                                        )}
                                    </InputGroup>
                                </Col>
                                <Col md={4} className="d-flex justify-content-end align-items-center">
                                    <div className="filter-info">
                                        <span className="badge-count">{filteredAvoirs.length}</span>
                                        <span className="text-muted ms-2">avoir(s) trouvé(s)</span>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Liste des avoirs avec pagination */}
                    <Card className="table-card border-0">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle custom-table">
                                    <thead>
                                        <tr>
                                            <th>N° Avoir</th>
                                            <SortableHeader field="date_facture">Date</SortableHeader>
                                            <th>Client</th>
                                            {/* <th>Motif</th> */}
                                            <th className="text-end">Montant HT</th>
                                            <th className="text-end">TVA ({taxes.tva_active ? taxes.tva_taux : 0}%)</th>
                                            <th className="text-end">CSS ({taxes.css_active ? taxes.css_taux : 0}%)</th>
                                            <SortableHeader field="montant">Total TTC</SortableHeader>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map((avoir) => (
                                                <tr key={avoir.id_facture} className="facture-row">
                                                    <td>
                                                        <a 
                                                            onClick={() => {
                                                                setSelectedAvoir(avoir);
                                                                fetchAvoirLignes(avoir.id_facture);
                                                                setShowModal(true);
                                                            }}
                                                            className="facture-link"
                                                            title={avoir.numero_facture}
                                                        >
                                                            <span className="facture-num">
                                                                <i className="ri-file-copy-line text-warning me-1"></i>
                                                                {avoir.numero_facture?.length > 8 
                                                                    ? avoir.numero_facture.substring(0, 8) + '...' 
                                                                    : avoir.numero_facture}
                                                            </span>
                                                        </a>
                                                    </td>
                                                    <td>
                                                        <span className="facture-date">
                                                            {formatDate(avoir.date_facture)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="client-info">
                                                            <span className="client-name">{avoir.client_nom}</span>
                                                            <small className="client-code">{avoir.code_client}</small>
                                                        </div>
                                                    </td>
                                                    {/* <td>
                                                        <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                                                            {avoir.notes && avoir.notes.length > 30 
                                                                ? avoir.notes.substring(0, 30) + '...' 
                                                                : avoir.notes || 'Avoir standard'}
                                                        </span>
                                                    </td> */}
                                                    <td className="text-end">
                                                        <span className="montant-ht">{formatCurrency(avoir.montant_ht)}</span>
                                                    </td>
                                                    <td className="text-end">
                                                        <span className="montant-tva text-warning">{formatCurrency(avoir.montant_tva)}</span>
                                                    </td>
                                                    <td className="text-end">
                                                        <span className="montant-css text-primary">{formatCurrency(avoir.montant_css)}</span>
                                                    </td>
                                                    <td className="text-end">
                                                        <span className="montant-ttc fw-bold text-danger">
                                                            -{formatCurrency(avoir.net_a_payer)}
                                                        </span>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="actions-container">
                                                            <ActionButton
                                                                variant="outline-info"
                                                                icon="ri-eye-line"
                                                                onClick={() => {
                                                                    setSelectedAvoir(avoir);
                                                                    fetchAvoirLignes(avoir.id_facture);
                                                                    setShowModal(true);
                                                                }}
                                                                tooltip="Voir détails"
                                                            />
                                                            <ActionButton
                                                                variant="outline-primary"
                                                                icon="ri-printer-line"
                                                                onClick={() => handlePrintAvoir(avoir)}
                                                                tooltip="Imprimer l'avoir"
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="text-center py-5">
                                                    <div className="empty-state">
                                                        <i className="ri-file-copy-line empty-icon text-warning"></i>
                                                        <h5 className="mt-3">Aucun avoir trouvé</h5>
                                                        <p className="text-muted mb-3">
                                                            {filterText 
                                                                ? "Aucun résultat ne correspond à votre recherche"
                                                                : "Aucun avoir n'a été émis pour le moment"}
                                                        </p>
                                                        {filterText && (
                                                            <Button 
                                                                variant="outline-primary"
                                                                onClick={() => setFilterText('')}
                                                            >
                                                                <i className="ri-refresh-line me-2"></i>
                                                                Réinitialiser la recherche
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-center py-3">
                                    <Pagination>
                                        <Pagination.Prev 
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        />
                                        {renderPagination()}
                                        <Pagination.Next 
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* Modal de détail avec lignes chargées */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered className="modern-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="ri-file-copy-line me-2 text-warning"></i>
                        Détail de l'avoir
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedAvoir && (
                        <>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <div>
                                    <h5 className="mb-1">{selectedAvoir.numero_facture}</h5>
                                    <p className="text-muted mb-0">
                                        <i className="ri-calendar-line me-1"></i>
                                        Date: {formatDate(selectedAvoir.date_facture)}
                                    </p>
                                </div>
                                <Badge bg="warning" className="px-4 py-2">
                                    <i className="ri-file-copy-line me-2"></i>
                                    Avoir
                                </Badge>
                            </div>

                            <div className="bg-light p-4 rounded-3 mb-4">
                                <h6 className="mb-3 text-primary">Client</h6>
                                <p className="mb-1 fw-semibold fs-5">{selectedAvoir.client_nom}</p>
                                <p className="text-muted mb-0">Code: {selectedAvoir.code_client}</p>
                                {selectedAvoir.client_telephone && (
                                    <p className="text-muted mb-0 mt-2">
                                        <i className="ri-phone-line me-2"></i>
                                        {selectedAvoir.client_telephone}
                                    </p>
                                )}
                            </div>

                            {selectedAvoir.notes && (
                                <div className="bg-light p-4 rounded-3 mb-4">
                                    <h6 className="mb-3 text-primary">Motif</h6>
                                    <p className="mb-0">{selectedAvoir.notes}</p>
                                </div>
                            )}

                            <h6 className="mb-3">Lignes de l'avoir</h6>
                            {loadingLignes ? (
                                <div className="text-center py-4">
                                    <Spinner animation="border" variant="primary" size="sm" />
                                    <p className="mt-2 text-muted">Chargement des lignes...</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <Table striped className="mb-4 modern-table">
                                        <thead>
                                            <tr>
                                                <th>Référence</th>
                                                <th>Produit</th>
                                                <th className="text-center">Quantité</th>
                                                <th className="text-end">Prix HT</th>
                                                <th className="text-end">Total HT</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedAvoirLignes.length > 0 ? (
                                                selectedAvoirLignes.map((ligne, idx) => (
                                                    <tr key={idx}>
                                                        <td>
                                                            <Badge bg="light" text="dark" className="font-mono">
                                                                {ligne.reference}
                                                            </Badge>
                                                        </td>
                                                        <td>{ligne.designation}</td>
                                                        <td className="text-center">{ligne.quantite}</td>
                                                        <td className="text-end">{formatCurrency(ligne.prix_unitaire_ht)}</td>
                                                        <td className="text-end fw-bold">
                                                            {formatCurrency(ligne.quantite * ligne.prix_unitaire_ht)}
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="5" className="text-center py-3">
                                                        <p className="text-muted mb-0">Aucune ligne disponible</p>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            )}

                            <div className="bg-light p-4 rounded-3">
                                <h6 className="mb-3 text-primary">Récapitulatif</h6>
                                <div className="d-flex justify-content-between mb-2">
                                    <span>Total HT</span>
                                    <span className="fw-bold">{formatCurrency(selectedAvoir.montant_ht)}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span>TVA ({taxes.tva_active ? taxes.tva_taux : 0}%)</span>
                                    <span className="fw-bold text-warning">{formatCurrency(selectedAvoir.montant_tva)}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span>CSS ({taxes.css_active ? taxes.css_taux : 0}%)</span>
                                    <span className="fw-bold text-info">{formatCurrency(selectedAvoir.montant_css)}</span>
                                </div>
                                <hr />
                                <div className="d-flex justify-content-between">
                                    <span className="h5">Total TTC</span>
                                    <span className="h5 text-danger">-{formatCurrency(selectedAvoir.net_a_payer)}</span>
                                </div>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Fermer
                    </Button>
                    <Button 
                        variant="warning" 
                        onClick={() => handlePrintAvoir(selectedAvoir)}
                    >
                        <i className="ri-printer-line me-2"></i>
                        Imprimer
                    </Button>
                </Modal.Footer>  
            </Modal>

            <style>{`
                /* Styles généraux */
                .fade-in {
                    animation: fadeIn 0.5s ease-out;
                }
                
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                /* En-tête */
                .header-section {
                    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
                    padding: 1.5rem;
                    border-radius: 20px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                }

                .page-icon {
                    width: 60px;
                    height: 60px;
                    background: linear-gradient(135deg, #4361ee20, #80b3ff20);
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 2rem;
                }

                .page-title {
                    font-size: 1.8rem;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 0;
                }

                .page-subtitle {
                    color: #64748b;
                    font-size: 0.95rem;
                }

                .btn-gradient {
                    background: linear-gradient(135deg, #4361ee, #3a56d4);
                    border: none;
                    color: white;
                    transition: all 0.3s ease;
                }

                .btn-gradient:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 16px #4361ee40;
                }

                /* Cartes statistiques */
                .stats-row {
                    margin-top: 1.5rem;
                }

                .stat-card {
                    border-radius: 20px !important;
                    overflow: hidden;
                    transition: all 0.3s ease;
                    background: white;
                    border: none;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.05) !important;
                }

                .stat-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 12px 24px rgba(0,0,0,0.1) !important;
                }

                .stat-icon-wrapper {
                    width: 60px;
                    height: 60px;
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 8px 16px rgba(0,0,0,0.1);
                }

                .stat-icon-wrapper.bg-warning { background: linear-gradient(135deg, #f59e0b, #d97706); }
                .stat-icon-wrapper.bg-info { background: linear-gradient(135deg, #3b82f6, #2563eb); }
                .stat-icon-wrapper.bg-primary { background: linear-gradient(135deg, #4361ee, #3a56d4); }
                .stat-icon-wrapper.bg-success { background: linear-gradient(135deg, #10b981, #059669); }

                .stat-content {
                    flex: 1;
                }

                .stat-label {
                    font-size: 0.75rem;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                    color: #64748b;
                    text-transform: uppercase;
                }

                .stat-value {
                    font-size: 1rem;
                    font-weight: 700;
                    line-height: 1.2;
                }

                .stat-trend {
                    font-size: 0.8rem;
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                }

                .text-warning { color: #f59e0b; }
                .text-info { color: #3b82f6; }
                .text-primary { color: #4361ee; }
                .text-success { color: #10b981; }

                /* Filtres */
                .filter-card {
                    border-radius: 16px !important;
                    background: white;
                }

                .search-input .input-group-text {
                    background: white;
                    border-right: none;
                    color: #64748b;
                }

                .search-input .form-control {
                    border-left: none;
                    padding: 0.6rem 0.75rem;
                }

                .search-input .form-control:focus {
                    border-color: #dee2e6;
                    box-shadow: none;
                }

                .filter-select {
                    border-radius: 12px;
                    padding: 0.6rem 1rem;
                    border: 2px solid #e2e8f0;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .filter-select:focus {
                    border-color: #4361ee;
                    box-shadow: 0 0 0 3px #4361ee20;
                }

                .badge-count {
                    background: linear-gradient(135deg, #4361ee, #3a56d4);
                    color: white;
                    padding: 0.3rem 0.8rem;
                    border-radius: 20px;
                    font-weight: 600;
                    font-size: 0.9rem;
                }

                /* Tableau */
                .table-card {
                    border-radius: 16px !important;
                    overflow: hidden;
                    background: white;
                }

                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.85rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem;
                    border-bottom: 2px solid #e2e8f0;
                }

                .sortable-header {
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .sortable-header:hover {
                    background: #f1f5f9;
                }

                .custom-table tbody td {
                    padding: 1rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #e2e8f0;
                }

                .facture-row {
                    transition: all 0.2s ease;
                }

                .facture-row:hover {
                    background: #f8fafc !important;
                    transform: scale(1.01);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
                }

                .facture-link {
                    text-decoration: none;
                    font-weight: 600;
                    color: #1e293b;
                    position: relative;
                    cursor: pointer;
                }

                .facture-link:hover {
                    color: #4361ee;
                }

                .facture-link::after {
                    content: '';
                    position: absolute;
                    bottom: -2px;
                    left: 0;
                    width: 0;
                    height: 2px;
                    background: #4361ee;
                    transition: width 0.3s ease;
                }

                .facture-link:hover::after {
                    width: 100%;
                }

                .client-info {
                    display: flex;
                    flex-direction: column;
                }

                .client-name {
                    font-weight: 500;
                    color: #1e293b;
                }

                .client-code {
                    color: #64748b;
                    font-size: 0.8rem;
                }

                .montant-ht, .montant-ttc, .montant-regle, .montant-reste {
                    font-weight: 500;
                }

                /* Actions */
                .actions-container {
                    display: flex;
                    gap: 0.5rem;
                    justify-content: center;
                }

                .action-btn {
                    width: 36px;
                    height: 36px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }

                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }

                .action-btn.btn-outline-info:hover {
                    background: linear-gradient(135deg, #0891b2, #0e7490);
                    border-color: #0891b2;
                    color: white;
                }

                .action-btn.btn-outline-primary:hover {
                    background: linear-gradient(135deg, #4361ee, #3a56d4);
                    border-color: #4361ee;
                    color: white;
                }

                /* Pagination */
                .pagination {
                    gap: 5px;
                }

                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                }

                .pagination .page-item.active .page-link {
                    background: linear-gradient(135deg, #4361ee, #3a56d4);
                    border-color: #4361ee;
                    color: white;
                }

                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                }

                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }

                /* Empty state */
                .empty-state {
                    padding: 3rem;
                    text-align: center;
                }

                .empty-icon {
                    font-size: 4rem;
                    color: #cbd5e1;
                    margin-bottom: 1.5rem;
                    animation: bounce 2s infinite;
                }

                @keyframes bounce {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-10px); }
                }

                /* Loading */
                .loading-container {
                    padding: 3rem;
                    text-align: center;
                }

                .loading-container .spinner-border {
                    width: 3rem;
                    height: 3rem;
                }

                /* Responsive */
                @media (max-width: 768px) {
                    .page-title {
                        font-size: 1.4rem;
                    }
                    
                    .page-icon {
                        width: 48px;
                        height: 48px;
                        font-size: 1.5rem;
                    }
                    
                    .stat-value {
                        font-size: 1rem;
                    }
                    
                    .actions-container {
                        flex-wrap: wrap;
                    }
                    
                    .pagination .page-link {
                        padding: 0.3rem 0.6rem;
                        font-size: 0.85rem;
                    }
                }
            `}</style>
        </React.Fragment>
    );
}